import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
    timeout: 60000, // Default timeout for most requests
    headers: {
        'Content-Type': 'application/json',
    },
});

// Create separate instance for file uploads with longer timeout
const uploadApi = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
    timeout: 10 * 60 * 1000, // 10 minutes for large file uploads
    headers: {
        'Content-Type': 'multipart/form-data',
    },
});

// Request interceptor to add auth token (for both APIs)
const addAuthInterceptor = (instance) => {
    instance.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('deepwave_token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );
};

// Response interceptor for error handling (for both APIs)
const addResponseInterceptor = (instance) => {
    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            // Handle common error scenarios
            if (error.response) {
                // Server responded with error status
                const { status, data } = error.response;
                
                switch (status) {
                    case 401: {
                        // Unauthorized - clear token and redirect to login
                        // Don't redirect from public-accessible pages
                        const publicPaths = ['/login', '/register', '/subscriptions', '/'];
                        const isPublicPath = publicPaths.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'));
                        localStorage.removeItem('deepwave_token');
                        localStorage.removeItem('deepwave_user');
                        if (!isPublicPath) {
                            window.location.href = '/login';
                        }
                        break;
                    }
                    case 403:
                        // Forbidden
                        console.error('Access forbidden:', data.message);
                        break;
                    case 429:
                        // Rate limited
                        console.error('Rate limit exceeded:', data.message);
                        break;
                    case 500:
                        // Server error
                        console.error('Server error:', data.message);
                        break;
                    default:
                        console.error('API Error:', data.message || 'Unknown error');
                }
                
                return Promise.reject(error.response.data);
            } else if (error.request) {
                // Network error or timeout
                console.error('Network error:', error.message);
                let errorMessage = 'Network error. Please check your connection.';
                
                // Provide more specific error messages for timeouts
                if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                    errorMessage = 'Request timeout. The upload is taking longer than expected. Please try uploading fewer images at once or check your connection.';
                }
                
                return Promise.reject({
                    success: false,
                    message: errorMessage,
                });
            } else {
                // Other error
                console.error('Request error:', error.message);
                return Promise.reject({
                    success: false,
                    message: 'Request failed. Please try again.',
                });
            }
        }
    );
};

// Apply interceptors to both instances
addAuthInterceptor(api);
addResponseInterceptor(api);
addAuthInterceptor(uploadApi);
addResponseInterceptor(uploadApi);

// Authentication API calls
export const authAPI = {
    // Register new user
    register: async (userData) => {
        try {
            const response = await api.post('/auth/register', userData);
            if (response.data.token) {
                localStorage.setItem('deepwave_token', response.data.token);
                localStorage.setItem('deepwave_user', JSON.stringify(response.data.user));
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Login user
    login: async (credentials) => {
        try {
            const response = await api.post('/auth/login', credentials);
            if (response.data.token) {
                localStorage.setItem('deepwave_token', response.data.token);
                localStorage.setItem('deepwave_user', JSON.stringify(response.data.user));
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Logout user
    logout: async () => {
        try {
            await api.post('/auth/logout');
            localStorage.removeItem('deepwave_token');
            localStorage.removeItem('deepwave_user');
            return { success: true };
        } catch (error) {
            // Clear local storage even if API call fails
            localStorage.removeItem('deepwave_token');
            localStorage.removeItem('deepwave_user');
            throw error;
        }
    },

    // Get current user
    getCurrentUser: async () => {
        try {
            const response = await api.get('/auth/me');
            localStorage.setItem('deepwave_user', JSON.stringify(response.data.user));
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

// Profile API calls
export const profileAPI = {
    // Get public profile by username
    getProfile: async (username) => {
        try {
            const response = await api.get(`/profile/${username}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Update own profile
    updateProfile: async (profileData) => {
        try {
            const response = await api.put('/profile', profileData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Toggle artist status
    toggleArtistStatus: async () => {
        try {
            const response = await api.put('/profile/artist-status');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Search profiles
    searchProfiles: async (searchTerm, limit = 20) => {
        try {
            const response = await api.get('/profile/search', {
                params: { q: searchTerm, limit },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Delete profile
    deleteProfile: async () => {
        try {
            const response = await api.delete('/profile');
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

// Follow API functions
export const followAPI = {
    // Follow a user
    followUser: async (userId) => {
        try {
            const response = await api.post(`/follow/${userId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Unfollow a user
    unfollowUser: async (userId) => {
        try {
            const response = await api.delete(`/follow/${userId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get user's following list with recent media
    getFollowing: async (userId) => {
        try {
            const response = await api.get(`/follow/following/${userId}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Get user's followers list
    getFollowers: async (userId) => {
        try {
            const response = await api.get(`/follow/followers/${userId}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Check if current user follows another user
    checkFollowStatus: async (userId) => {
        try {
            const response = await api.get(`/follow/status/${userId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get recent activity from followed users
    getFollowingActivity: async (userId) => {
        try {
            const response = await api.get(`/follow/activity/${userId}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Get follow stats for a user
    getFollowStats: async (userId) => {
        try {
            const response = await api.get(`/follow/stats/${userId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get suggested users to follow
    getSuggested: async (limit = 10) => {
        try {
            const response = await api.get('/follow/suggested', {
                params: { limit }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

// Notification API calls
export const notificationAPI = {
    getRecent: async () => {
        try {
            const response = await api.get('/notifications/recent');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getUnreadCount: async () => {
        try {
            const response = await api.get('/notifications/unread-count');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getAll: async (page = 1, limit = 20) => {
        try {
            const response = await api.get('/notifications', { params: { page, limit } });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    markAsRead: async (id) => {
        try {
            const response = await api.put(`/notifications/${id}/read`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    markAllAsRead: async () => {
        try {
            const response = await api.put('/notifications/read-all');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getPreferences: async () => {
        try {
            const response = await api.get('/notifications/preferences');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updatePreference: async (type, isEnabled) => {
        try {
            const response = await api.put(`/notifications/preferences/${type}`, { isEnabled });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

// Admin notification management API calls
export const adminNotificationAPI = {
    getTypes: async () => {
        try {
            const response = await api.get('/admin/notifications/types');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updateType: async (type, isActive) => {
        try {
            const response = await api.put(`/admin/notifications/types/${type}`, { isActive });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getRecipientCount: async (target) => {
        try {
            const response = await api.get('/admin/notifications/recipients', { params: { target } });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    send: async (payload) => {
        try {
            const response = await api.post('/admin/notifications/send', payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

// Utility functions
export const apiUtils = {
    // Check if user is authenticated
    isAuthenticated: () => {
        const token = localStorage.getItem('deepwave_token');
        return !!token;
    },

    // Get stored user data
    getStoredUser: () => {
        const userStr = localStorage.getItem('deepwave_user');
        try {
            return userStr ? JSON.parse(userStr) : null;
        } catch (error) {
            console.error('Error parsing stored user:', error);
            return null;
        }
    },

    // Clear authentication data
    clearAuth: () => {
        localStorage.removeItem('deepwave_token');
        localStorage.removeItem('deepwave_user');
    },

    // Format error message for display
    formatErrorMessage: (error) => {
        if (typeof error === 'string') {
            return error;
        }
        if (error?.message) {
            return error.message;
        }
        if (error?.data?.message) {
            return error.data.message;
        }
        return 'An unexpected error occurred';
    },
};

// Export uploadApi for multipart form data uploads with longer timeout
export { uploadApi };

export default api;